import React from 'react';
import { Steps as AntSteps, StepsProps as AntStepsProps, StepProps as AntStepProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface StepsProps extends AntStepsProps {
  className?: string;
}

export interface StepProps extends AntStepProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  steps: {
    '&.ant-steps': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.regular,
    },
    // Step icon styling
    '& .ant-steps-item-icon': {
      borderWidth: theme.border.width.sm,
      borderRadius: theme.radius.full,
    },
    // Wait status (not yet reached)
    '& .ant-steps-item-wait .ant-steps-item-icon': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      borderColor: theme.color.semantic.border.primary,
      color: theme.color.semantic.text.secondary,
    },
    '& .ant-steps-item-wait .ant-steps-item-title': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
    '& .ant-steps-item-wait .ant-steps-item-description': {
      color: `${theme.color.semantic.text.tertiary} !important`,
    },
    // Process status (current step)
    '& .ant-steps-item-process .ant-steps-item-icon': {
      backgroundColor: theme.color.semantic.brand.default.primary,
      borderColor: theme.color.semantic.brand.default.primary,
      color: theme.color.semantic.text.inverse,
    },
    '& .ant-steps-item-process .ant-steps-item-title': {
      fontWeight: theme.typography.fontWeight.semibold,
      color: `${theme.color.semantic.text.primary} !important`,
    },
    '& .ant-steps-item-process .ant-steps-item-description': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
    // Finish status (completed step)
    '& .ant-steps-item-finish .ant-steps-item-icon': {
      backgroundColor: theme.color.semantic.accent.green.strong,
      borderColor: theme.color.semantic.accent.green.strong,
      color: theme.color.semantic.text.inverse,
      '& .ant-steps-icon': {
        color: theme.color.semantic.text.inverse,
      },
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
      },
    },
    '& .ant-steps-item-finish .ant-steps-item-title': {
      color: `${theme.color.semantic.text.primary} !important`,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '& .ant-steps-item-finish .ant-steps-item-description': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
    // Error status
    '& .ant-steps-item-error .ant-steps-item-icon': {
      backgroundColor: theme.color.semantic.brand.danger.primary,
      borderColor: theme.color.semantic.brand.danger.primary,
      color: theme.color.semantic.text.inverse,
      '& .ant-steps-icon': {
        color: theme.color.semantic.text.inverse,
      },
      '& .anticon': {
        color: `${theme.color.semantic.text.inverse} !important`,
      },
    },
    '& .ant-steps-item-error .ant-steps-item-title': {
      color: `${theme.color.semantic.brand.danger.primary} !important`,
      fontWeight: theme.typography.fontWeight.medium,
    },
    '& .ant-steps-item-error .ant-steps-item-description': {
      color: `${theme.color.semantic.text.secondary} !important`,
    },
    // Title and description
    '& .ant-steps-item-title': {
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: `${theme.color.semantic.text.primary} !important`,
      lineHeight: theme.typography.lineHeight.normal,
    },
    '& .ant-steps-item-description': {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.regular,
      color: `${theme.color.semantic.text.secondary} !important`,
      lineHeight: theme.typography.lineHeight.normal,
    },
    // Tail (connecting line)
    '& .ant-steps-item-tail::after': {
      backgroundColor: theme.color.semantic.border.primary,
      height: theme.border.width.sm,
    },
    '& .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-tail::after': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
    '& .ant-steps-item-finish > .ant-steps-item-container > .ant-steps-item-content > .ant-steps-item-title::after': {
      backgroundColor: theme.color.semantic.brand.default.primary,
    },
    '&.ant-steps .ant-steps-item:not(.ant-steps-item-active):not(.ant-steps-item-process) > .ant-steps-item-container[role=\'button\']:hover .ant-steps-item-icon': {
      borderColor: theme.color.semantic.brand.default.primary,
      '& .ant-steps-icon': {
        color: theme.color.semantic.brand.default.primary,
      },
    },
  },
  stepsHorizontal: {
    '&.ant-steps-horizontal': {},
  },
  stepsVertical: {
    '&.ant-steps-vertical': {},
  },
  stepsSmall: {
    '&.ant-steps-small': {
      fontSize: theme.typography.fontSize.xs,
      fontWeight: theme.typography.fontWeight.regular,
    },
  },
  stepsNavigation: {
    '&.ant-steps-navigation': {
      '& .ant-steps-item': {
        backgroundColor: theme.color.semantic.background.neutral.subtle,
        borderColor: theme.color.semantic.border.primary,
        borderWidth: theme.border.width.sm,
        '&:hover': {
          backgroundColor: theme.color.semantic.brand.default.subtle,
          borderColor: theme.color.semantic.brand.default.primary,
          '& .ant-steps-item-title': {
            color: theme.color.semantic.brand.default.primary,
          },
        },
      },
      '& .ant-steps-item-active': {
        backgroundColor: theme.color.semantic.brand.default.primary,
        borderColor: theme.color.semantic.brand.default.primary,
        '& .ant-steps-item-title': {
          color: theme.color.semantic.text.inverse,
        },
      },
    },
  },
}));

const StepsInner: React.FC<StepsProps> = ({
  direction = 'horizontal',
  size = 'default',
  type,
  className = '',
  ...rest
}) => {
  const classes = useStyles();

  const getDirectionClass = () => {
    switch (direction) {
      case 'horizontal':
        return classes.stepsHorizontal;
      case 'vertical':
        return classes.stepsVertical;
      default:
        return classes.stepsHorizontal;
    }
  };

  const getSizeClass = () => {
    return size === 'small' ? classes.stepsSmall : '';
  };

  const getTypeClass = () => {
    return type === 'navigation' ? classes.stepsNavigation : '';
  };

  const combinedClassName = `${classes.steps} ${getDirectionClass()} ${getSizeClass()} ${getTypeClass()} ${className}`.trim();

  return (
    <AntSteps
      direction={direction}
      size={size}
      type={type}
      {...rest}
      className={combinedClassName}
    />
  );
};

// Create Steps with Step property
export const Steps = Object.assign(StepsInner, {
  Step: AntSteps.Step,
});

export default Steps;
